import classNames from 'classnames'
import moment from 'moment'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
// import PropTypes from 'prop-types';
import type { Moment } from 'moment'
import { globalConfig } from '../../wui-provider/src'
import type {SizeType} from '../../wui-core/src/types/iCore';
import { ConfigContext, WithConfigConsumer } from '../../wui-provider/src/context'
import { cssUtil, formatUtils, getChildrenText, getNid, prefix, setComponentClass, setComponentSize, Warning, WebUI } from './../../wui-core/src'
import { getLangInfo } from './../../wui-datepicker/src/_utils/lang'
import i18n from './i18n'
import type { TimePickerDefaultProps, TimePickerProps } from './iTimePicker'
import RcTimePicker from './rc/index'

const {isShouldUpdate} = Warning
const prefixCls = `${prefix}-time-picker`

const defaultProps: TimePickerDefaultProps = {
    size: 'md',
    allowClear: true, // 默认允许清除
    focusOnOpen: false,
    browser: false,
    autoComplete: 'off',
    placeholder: '',
    locale: 'zh-cn',
    clearIcon: <i className='uf uf-close-c' />
}

const TimePicker: React.FC<TimePickerProps> = (props) => {
    const context = useContext(ConfigContext)
    // 获取format
    const getFormat = useCallback((props: TimePickerProps) => {
        let {format} = props
        if (format) return format;

        let showTime: any = {};
        const globalDataFormat = globalConfig().getGlobalDataFormat()
        if (!format && globalDataFormat) {
            const {timeFormat} = globalDataFormat
            format = formatUtils.diworkFormat2Picker(timeFormat, 'time').format
            if (format) {
                showTime = formatUtils.format2ShowTime(format);
            }
        }

        // 未传入format时，showTime配置都默认为true【***不要合并props，逻辑更清晰***】
        let {use12Hours, showHour, showMinute, showSecond} = props;
        showHour = showHour ?? showTime.showHour ?? true;
        showMinute = showMinute ?? showTime.showMinute ?? true;
        showSecond = showSecond ?? showTime.showSecond ?? true;

        if (use12Hours) {
            const fmtString = [showHour ? 'h' : '', showMinute ? 'mm' : '', showSecond ? 'ss' : '']
                .filter(item => !!item)
                .join(':')
            return fmtString.concat(' A')
        }

        return [showHour ? 'HH' : '', showMinute ? 'mm' : '', showSecond ? 'ss' : ''].filter(item => !!item).join(':')
    }, [])

    // 初始化value
    const initValue = useCallback((value: TimePickerProps['value'], props: TimePickerProps) => {
        const format = getFormat(props)
        if (value) {
            if (typeof value === 'string' && moment(value, format).isValid()) {
                value = moment(value, format)
            } else if (typeof value !== 'string' && value.isValid?.()) {
                value = moment(value.format(format), format)
            } else {
                console.error('`value` of TimePicker is not in correct format')
                value = null
            }
        } else if (value === undefined) {
            value = undefined
        } else {
            value = null
        }
        // 确保value的moment实例多语版本一致
        if (value) {
            let langInfo = getLangInfo({format, locale: props.locale, localeData: i18n, componentName: 'timePicker'}) // 获取语言包信息
            moment.locale(langInfo.lang)
            value = moment(value, format)
        }
        return value
    }, [getFormat])

    const format = useMemo(() => getFormat(props), [props.use12Hours, props.showHour, props.showMinute, props.showSecond, props.format])

    const langInfo = useMemo(() =>
        getLangInfo({format, locale: props.locale, localeData: i18n, componentName: 'timePicker'}),
    [format, props.locale]
    )

    const [value, setValue] = useState<Moment | null | undefined>(() => initValue(props.value, props))
    const [defaultValue, setDefaultValue] = useState<Moment | null | undefined>(() => initValue(props.defaultValue, props))

    const showTime = useMemo(() => formatUtils.format2ShowTime(format), [format])

    // 处理 value\defaultValue 变化
    useEffect(() => {
        if ('value' in props) {
            const newValue = initValue(props.value, props);
            setValue(newValue);
        }
    }, [props.value, props.format, props.use12Hours, props.locale])

    // 处理 value\defaultValue 变化
    useEffect(() => {
        if ('defaultValue' in props) {
            const newDefaultValue = initValue(props.defaultValue, props);
            setDefaultValue(newDefaultValue);
        }
    }, [props.defaultValue, props.format, props.use12Hours, props.locale])

    const getPopupContainerDom = useCallback((dom: HTMLElement) => {
        const {getPopupContainer} = props
        if (typeof getPopupContainer === 'function') {
            return (getPopupContainer as (h?: HTMLElement) => HTMLElement)(dom)
        } else {
            return cssUtil.parentsUntil(dom)
        }
    }, [props.getPopupContainer])

    const handleTimeChange = useCallback((time: Moment | null) => {
        const {onChange} = props
        let timeString = ''
        if (time !== null && typeof time.format === 'function') {
            timeString = time.format(format)
        }
        setValue(time)
        onChange?.(time, timeString)
    }, [format, props.onChange])

    let {
        browser,
        fieldid,
        style,
        defaultOpenValue,
        showClear,
        allowClear,
        className,
        requiredStyle,
        bordered,
        align,
        use12Hours,
        placeholder,
        clearText,
        renderExtraFooter,
        addon,
        inputIcon,
        suffixIcon,
        clsPrefix,
        onChange,
        locale,
        size,
        placement = props.dir === "rtl" ? "bottomRight" : "bottomLeft",
        readOnly,
        ...other
    } = props

    // ConfigProvider的context配置
    bordered = bordered ?? context?.bordered ?? true // 接受provider控制

    // 浏览态
    const renderBrowser = useCallback((params: any) => {
        let {browserClassName, browserStyle, bordered, value, format} = params;

        const componentCls = classNames(`${prefixCls}-browser`, {
            [browserClassName]: browserClassName,
            [`${prefix}-component-browser-border-bottom`]: bordered === 'bottom'
        })

        const timeString = value && value.isValid() ? value.format(format) : '';
        const displayText = timeString || '--'; // 如果值为空，显示 '--'

        return (
            <div className={`${prefix}-component-browser`}>
                <div className={componentCls} style={browserStyle} fieldid={props.fieldid}>
                    {displayText}
                </div>
            </div>
        );
    }, [])

    isShouldUpdate('TimePicker', props)

    const globalDataFormat = globalConfig().getGlobalDataFormat()
    if (globalDataFormat) {
        const {timeFormat} = globalDataFormat
        use12Hours = use12Hours ?? formatUtils.diworkFormat2Picker(timeFormat, 'time').use12Hours
    }
    size = setComponentSize(size, {defaultIsMd: true}) as SizeType | undefined;

    if (browser) {
        return renderBrowser({size, className, style, bordered, align, requiredStyle, value, format})
    }

    // 兼容API
    const extra = {
        inputIcon: inputIcon || suffixIcon || <i className='uf uf-time-c-o' />,
        renderExtraFooter: renderExtraFooter || addon,
        allowClear: showClear !== undefined ? showClear : allowClear,
        className: classNames(className, {
            [`${prefixCls}-${size}`]: !!size,
            [`${prefixCls}-disabled`]: other.disabled,
            [`${prefixCls}-rtl`]: other.dir === 'rtl',
            ...setComponentClass({clsPrefix: prefixCls, bordered, align, requiredStyle, readOnly})
        })
    }
    let _clearText =
        getChildrenText(clearText).join('') ||
        (langInfo.langMap as {clear: string})?.clear

    let _defaultOpenValue = moment(defaultOpenValue, format).isValid() ? moment(defaultOpenValue, format) : moment().set({h: 0, m: 0, s: 0}) // 默认时分秒为 0点0分0秒
    let adapterNid = getNid(props)

    return (
        <RcTimePicker
            {...(!showTime ? {} : (showTime as any))}
            {...other}
            {...extra}
            fieldid={props.fieldid}
            inputReadOnly={readOnly}
            placement={placement}
            // transitionName='slide-up'
            getPopupContainer={getPopupContainerDom}
            prefixCls={clsPrefix}
            style={style}
            locale={langInfo} // 补充多语的locale
            placeholder={getChildrenText(placeholder).join('')}
            value={value}
            defaultValue={defaultValue}
            defaultOpenValue={_defaultOpenValue}
            format={format}
            use12Hours={use12Hours}
            onChange={handleTimeChange}
            clearText={_clearText}
            {...adapterNid}
        />
    )
}

// TimePicker.propTypes = propTypes;
export default WithConfigConsumer({name: 'timepicker'})(
    WebUI({name: 'time-picker', defaultProps})(TimePicker)
)
